import type {
  CriterionKey,
  RubricScores,
  VerdictCriterion,
} from '../../utils/evaluation-rubric';
import type {
  JudgeConfig,
  JudgeExpected,
  JudgeResult,
} from '../../utils/judges';
import type { EngineRequest } from './engine-trace-wire';
import type { ValidateTurn } from './validate-runs';
import { validateProject } from './validate-project';
import { describeSubmission } from './webview-submission';
import { bobcatApiUrl } from '../../utils/bobcat-api';

/**
 * Calls the `/ai/validate/*` routes the backend serves (`bobcatApiUrl`; its
 * `bobcat/src/server-only/ai/router.ts`).
 */

export interface ScoreResponse {
  scores: RubricScores;
  autoFail: string | null;
  summary: string;
  reasons: Partial<Record<CriterionKey, string>>;
  whyFailed?: string | null;
  toneNote?: string | null;
  /** The criteria it was scored on, when the solution edited its rubric. */
  criteria?: VerdictCriterion[];
}

export interface NextTurnResponse {
  message: string | null;
  pick: string | null;
  done: boolean;
  reason: string;
}

/** A little under the server function's 60s limit, so the browser says so first. */
const TIMEOUT_MS = 55_000;

const post = async <T>(
  path: string,
  body: unknown,
  signal?: AbortSignal
): Promise<T> => {
  const timeout = new AbortController();
  const timer = setTimeout(() => timeout.abort(), TIMEOUT_MS);
  const onAbort = () => timeout.abort();
  signal?.addEventListener('abort', onAbort);
  try {
    const response = await fetch(bobcatApiUrl(path), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
      signal: timeout.signal,
    });
    const json = await response.json().catch(() => null);
    if (!response.ok) {
      const b = json as { detail?: string; error?: string } | null;
      throw new Error(b?.detail ?? b?.error ?? response.statusText);
    }
    return json as T;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', onAbort);
  }
};

/** Only what the prompts read — latency and ids stay in the browser. */
export const wireTranscript = (transcript: ValidateTurn[]) =>
  transcript.map(
    ({
      role,
      text,
      options,
      seeded,
      system,
      agent,
      submitted,
      webview,
      embeds,
    }) => ({
      role,
      // What a webview handed over is part of what the customer did, and a
      // form the bot opened is part of its reply: without it, a reply that
      // only opens a form reads as an empty one.
      text: [
        text,
        ...(webview ? [`[Opens a form: ${webview.label || 'a webview'}]`] : []),
        ...(submitted ? [`[${describeSubmission(submitted)}]`] : []),
      ]
        .filter(Boolean)
        .join('\n'),
      ...(options?.length ? { options } : {}),
      ...(seeded ? { seeded: true } : {}),
      ...(system ? { system: true } : {}),
      ...(agent ? { agent: true } : {}),
      // Components the page cannot show; the judge is told they were sent.
      ...(embeds?.length ? { embeds } : {}),
    })
  );

/**
 * One score request. `judge` is the solution's Quality score judge
 * (`scoringJudgeOf`) and belongs to the request: the caller names it, never a
 * variable another render can change under a request already running. A
 * scenario or a suite names the judge it started with for every request of
 * the run; a Test chat, and a re-score of a saved run, name the judge current
 * when each request is issued, so a chat whose judge was edited midway has
 * replies scored by both. Null or absent while the judge has no rules or
 * examples: the request then carries no `judge` and runs on the default
 * model — the rubric as it comes.
 */
export const requestScore = (
  {
    judge,
    ...args
  }: {
    transcript: ValidateTurn[];
    focusIndex: number | null;
    scenario?: string;
    expectation?: string;
    judge?: JudgeConfig | null;
  },
  signal?: AbortSignal
) =>
  post<ScoreResponse>(
    '/ai/validate/score',
    {
      ...args,
      transcript: wireTranscript(args.transcript),
      ...(judge ? { judge } : {}),
    },
    signal
  );

/** One of the solution's judges on a whole conversation. */
export const requestJudge = (
  args: {
    judge: JudgeConfig;
    transcript: ValidateTurn[];
    scenario?: string;
    expectation?: string;
  },
  signal?: AbortSignal
) =>
  post<JudgeResult>(
    '/ai/validate/judge',
    { ...args, transcript: wireTranscript(args.transcript) },
    signal
  );

export interface ImportJudgeResponse {
  config: JudgeConfig;
  description: string;
}

/** An existing scorer prompt, turned into a judge. */
export const requestJudgeImport = (prompt: string, signal?: AbortSignal) =>
  post<ImportJudgeResponse>('/ai/validate/judge-import', { prompt }, signal);

export interface SuggestFixResponse {
  rule: string;
  why: string;
}

/** A rule that would make the judge agree with the team on a conversation. */
export const requestJudgeSuggest = (
  args: {
    judge: JudgeConfig;
    transcript: ValidateTurn[];
    team: JudgeExpected;
    result: { value: JudgeExpected; summary: string; reasons: string[] };
  },
  signal?: AbortSignal
) =>
  post<SuggestFixResponse>(
    '/ai/validate/judge-suggest',
    { ...args, transcript: wireTranscript(args.transcript) },
    signal
  );

export const requestNextTurn = (
  args: {
    scenario: string;
    expectation?: string;
    transcript: ValidateTurn[];
    turn: number;
    maxTurns: number;
  },
  signal?: AbortSignal
) =>
  post<NextTurnResponse>(
    '/ai/validate/next-turn',
    { ...args, transcript: wireTranscript(args.transcript) },
    signal
  );

export interface SeedTurnsResponse {
  messages: string[];
}

export const requestSeedTurns = (
  args: {
    scenario: string;
    expectation?: string;
    position: 'mid' | 'end';
  },
  signal?: AbortSignal
) => post<SeedTurnsResponse>('/ai/validate/seed-turns', args, signal);

/**
 * One customer message, posted by Pro Studio's server with the AI Gateway's
 * `x-ai-eg-model` override (the browser can't add that header to the
 * Engagement API). See `/ai/validate/send-message`.
 */
export const requestSendMessage = (
  args: {
    engagementApiUrl: string;
    chatId: string;
    accessToken: string;
    message: Record<string, unknown>;
    model: string;
  },
  signal?: AbortSignal
) => post<{ ok: true }>('/ai/validate/send-message', args, signal);

export type { EngineRequest, EngineStep } from './engine-trace-wire';

export interface EngineTraceResponse {
  requests: EngineRequest[];
  truncated: boolean;
}

/** What the bot engine walked for a chat, from the engine logs. */
/**
 * `projectId`: off localhost, logs are read only for chats in a project the
 * caller can open (`authorizeLogRead` in the router).
 */
export const requestEngineTrace = (
  args: { chatId: string; sinceMs: number },
  signal?: AbortSignal
) =>
  post<EngineTraceResponse>(
    '/ai/validate/engine-trace',
    { ...args, projectId: validateProject() },
    signal
  );

/* ---- Voice test calls (`bobcat/src/server-only/voice/test-call.ts`) ----------------- */

/**
 * A voice channel the browser can place a test call through: which channel,
 * and the number it rings from. It holds no Twilio credential — the server
 * reads the channel's account itself (`voiceAccess` in
 * `bobcat/src/server-only/ai/router.ts`), so nothing here can put a token on the wire.
 */
export interface VoiceLine {
  /** The channel's stream id; the server finds the channel by it. */
  channelId: string;
  number: string;
  /** The channel's integrator id, from its voice webhook — how its calls are found in the logs. */
  integratorId?: string;
}

export type TestCallStatus =
  | 'queued'
  | 'ringing'
  | 'in-progress'
  | 'completed'
  | 'busy'
  | 'failed'
  | 'no-answer'
  | 'canceled';

export interface TestCallResponse {
  callSid: string;
  status: TestCallStatus;
  duration?: number;
}

/**
 * The channel a voice route acts on. Read when the request is made, because
 * `projectId` is set by `ValidateStage` after this module loads. With no
 * project open it is dropped from the body and the server answers 400 for it.
 */
const channelOf = (line: VoiceLine) => ({
  projectId: validateProject(),
  channelId: line.channelId,
});

export const requestTestCall = (line: VoiceLine, to: string) =>
  post<TestCallResponse>('/ai/voice/call-me', { ...channelOf(line), to });

export const requestTestCallStatus = (line: VoiceLine, callSid: string) =>
  post<TestCallResponse>('/ai/voice/call-status', {
    ...channelOf(line),
    callSid,
  });

export const requestHangUp = (line: VoiceLine, callSid: string) =>
  post<TestCallResponse>('/ai/voice/hang-up', {
    ...channelOf(line),
    callSid,
  });

/* ---- A "Call me" call's conversation, from the logs ---------------------- */

export interface CallRequest {
  at: number;
  userInput?: string;
  lastSaid?: string;
  stopNode?: number;
}

export interface CallEvent {
  kind:
    | 'transfer-started'
    | 'agent-answered'
    | 'no-destination'
    | 'conference-ended';
  at: number;
}

export interface CallTranscriptResponse {
  chatId: string | null;
  requests: CallRequest[];
  events: CallEvent[];
}

export const requestCallTranscript = (args: {
  /** A phone call's voice channel; not needed when `chatId` is known. */
  integratorId?: string | null;
  sinceMs: number;
  chatId?: string | null;
}) =>
  post<CallTranscriptResponse>('/ai/validate/call-transcript', {
    ...(args.integratorId ? { integratorId: args.integratorId } : {}),
    sinceMs: args.sinceMs,
    projectId: validateProject(),
    ...(args.chatId ? { chatId: args.chatId } : {}),
  });
